import React from 'react';
import BlogLayout from '../../components/BlogLayout';

const CaughtDoingItRight = () => {
    return (
        <BlogLayout
            title="We Messed Up In Public. Here's What We Did About It."
            date="Mar 02, 2026"
            readTime={4}
            heroImage="/images/blog/caught-doing-it-right-hero.png"
            description="How RoofClaw's AI agent sent 4 duplicate emails to a client in one morning — and the 20-minute fix that made every future agent better."
        >
            <p className="lead text-xl text-[#F0EFF4] font-medium">
                Adam Sand has a policy called "Caught Doing It Right." The idea is simple: when you mess up, don't hide it. Own it, explain it, fix it publicly, and let the lesson make everything better. This is one of those posts.
            </p>
            <p>
                Today, our AI sales agent sent a client four nearly identical emails in one morning.
            </p>
            <p>
                We're telling you exactly what happened, why, and how we fixed it in 20 minutes.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">What Happened</h2>
            <p>
                RoofClaw runs on OpenClaw — a self-hosted AI agent that lives on a dedicated Mac in your office. Part of our system is an inbox monitor: a cron job that fires every 15 minutes, checks for client replies, and responds promptly in Vito's voice.
            </p>
            <p>
                When a client replied to their onboarding email with a couple of setup questions, the inbox monitor did exactly what it was supposed to do. It found the unread email. It read the questions. It wrote a clear, helpful reply. It sent it.
            </p>
            <p>
                Then 15 minutes later, it ran again. Found the same email — still unread. Replied again.
            </p>
            <p>
                And again. And again.
            </p>
            <p>
                Four times. Same questions. Same answers. Four separate emails.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">The Root Cause</h2>
            <p className="font-bold text-[#F0EFF4]">
                One missing step: we never marked the email as read after replying.
            </p>
            <p>
                It sounds obvious in hindsight. But when you're building fast and shipping fast, you can miss the obvious things. The inbox monitor was doing its job — finding unread emails and responding. The problem was purely in what happened <em>after</em> the response was sent.
            </p>
            <p>
                The email stayed unread. The next cycle found it again. Classic automation loop.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">The Fix</h2>
            <p>
                The moment we identified the issue, we shut down all email crons immediately. No more sends until the fix was in.
            </p>
            <p className="font-bold mb-4">The solution had two layers:</p>
            <ul className="list-disc pl-6 mb-8 text-[#F0EFF4]/80 space-y-4">
                <li><strong className="text-[#F0EFF4]">Layer 1 — Mark as read:</strong> After sending any reply, the agent now immediately marks the email thread as read via the Gmail API (<code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">gog gmail thread modify [threadId] --remove UNREAD</code>). The next cycle won't see it.</li>
                <li><strong className="text-[#F0EFF4]">Layer 2 — Local thread ID log:</strong> We added a <code className="font-data bg-[#18181B] px-1 py-0.5 rounded text-sm text-[#7B61FF]">replied-threads.json</code> file that records every thread ID we've replied to. Even if the Gmail mark-as-read call fails for any reason, the local log catches it. Both layers have to fail simultaneously for a duplicate to slip through.</li>
            </ul>
            <p>
                Belt and suspenders.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16 group-hover:text-[#7B61FF] transition-colors">What We Learned</h2>
            <p>
                This lesson is now baked into the RoofClaw build standard. Every agent we ship will have this pattern from day one: when you process an email, mark it read. When you send a reply, log the thread ID. Don't assume the inbox will tell you what you've already done.
            </p>
            <p>
                One more thing: our client was gracious about it. We were embarrassed. Adam handled it personally and immediately. That's the right move — not hiding behind the AI, not making excuses, just owning it and fixing it.
            </p>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7B61FF] opacity-10 blur-[100px] rounded-full mix-blend-screen pointer-events-none"></div>
                <h2 className="text-3xl font-heading font-bold mb-6">The Bottom Line</h2>
                <p className="text-lg text-[#F0EFF4]/80 mb-6">
                    Building AI agents in public means your mistakes are visible. We think that's a feature, not a bug. Every stumble we catch and document makes the product more reliable for the next client.
                </p>
                <p className="text-xl font-drama italic text-[#7B61FF]">
                    If you're building automation that touches email, learn from our morning: <strong className="font-heading not-italic text-[#F0EFF4]">always mark as read.</strong> You're welcome.
                </p>
            </div>

        </BlogLayout>
    );
};

export default CaughtDoingItRight;
